/* eslint-disable no-secrets/no-secrets */
/* eslint-disable padding-line-between-statements */
import { useModal } from '@jutro/components';
import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react'
import _, { filter } from 'lodash'
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import RoofDetails from '../RoofDetails/RoofDetails';
import metadata from './ENTRoofUpdatesComponent.metadata.json5';
import TWIADateFormatUtil from '../../../util/TWIADateFormatUtil';
import styles from '../Construction.module.scss';
import messages from '../Construction.messages';
import { getHOLOB, getHOLOBPath } from '../../../util/EntHomeownersUtil';

import { Button } from '@jutro/legacy/components';

// 2024-06-21 - Debra Frum - US26267 - Commercial Quote - Buildings and Locations – Construction Screen – Roofing Updates
//   Moved Roofing Updates component from Residential capability to a common capability so that it cam be shared with 
//   multiple LOBs  
//   Modified component to handle Commercial Property in addition to Homeowners LOB

// 2024-06-25 - Debra Frum - US26253 - Commercial Quote - Construction - Roofing Updates - Validations
//   Added page level validations to this Roofing Updates component currently used by the TWIA Commercial Construction page
//   Modified the TWIA Residential Construction page to use this common Roofing Updates component

function ENTRoofUpdatesComponent(props) {
    const { submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate, selectedBuilding, isCommercial } = props;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [showRoofDetails, updateShowRoofDetails] = useState(false);
    const [roofDetailsVM, updateRoofDetailsVM] = useState();
    const [checkbox, setCheckBox] = useState(isCommercial ? true : false);
    const [roofData, setRoofData] = useState([]);
    const [editRowIndex, setEditRowIndex] = useState(null);
    const { refreshData } = useDataRefresh();
    const { showAlert, showConfirm } = useModal();
    const intl = useContext(IntlContext);
    
    const getPolicyCode = () => {
        if (isTFPA && jobType) {
            return _.get(submissionVM,  'hoLOB.coverables.policyType_Ext.value.code');
        }
        return isCommercial ? '' : hoLOB?.policyType_TWIA?.value?.code;
    }

    // US26832 is this a TFPA transaction
    const isTFPA = _.get(submissionVM.value, 'baseData.associationName_TWIA') === 'tfpa';
    const jobType = _.get(submissionVM.value, 'baseData.jobType') === 'PolicyChange';

    // Homeowners
    const hoLOB = getHOLOB(submissionVM);
    const hoLOBPath = getHOLOBPath(submissionVM);
    const structureTypeValue = isCommercial ? '' : _.get(hoLOB, 'coverables.yourHome.structureType_TWIA.value.code');
    const policyTypeCode = getPolicyCode();
    const structureTypes = ['DwellingOutBuild', 'IndividuallyTH', '1Family', '2Family', 'PublicHousing']; // DE15630 // DE15951
    // US26832 show roof type switches between TFPA and TWIA
    const getShowRoofType = () => {
        if (isTFPA && jobType) {
            return _.get(submissionVM,  'hoLOB.coverables.policyType_Ext.value.code');
        }
        return policyTypeCode === 'HO3' || policyTypeCode === 'DP2' || (policyTypeCode === 'TWRD' && _.includes(structureTypes, structureTypeValue));
    }
    const showRoofType = getShowRoofType();

    // Commercial Property
    const coverageFormCode = isCommercial ? selectedBuilding?.coverageForm.value.code : '';
    
    const getRoofUpgradeExists = () => {
        if (isTFPA && jobType) {
            return _.get(hoLOB, 'coverables_Ext.construction.roofUpgradeExists.value')
        }
        return isCommercial 
            ? selectedBuilding?.construction_TWIA?.roofUpdateDtls_TWIA?.length > 0 ? true : false
            : _.get(hoLOB, 'coverables.construction.roofUpgradeExists.value')
    }
    
    const getRoofTableData = () => {
        if (isTFPA && jobType) {
            return _.get(hoLOB, 'coverables_Ext.construction.roofUpdateDtls_TWIA.value')
        }
        return isCommercial
            ? selectedBuilding.construction_TWIA?.roofUpdateDtls_TWIA?.value
            : _.get(hoLOB, 'coverables.construction.roofUpdateDtls_TWIA.value')
    }

    const roofUpgradeExists = getRoofUpgradeExists();
    const roofTableData = getRoofTableData();

    // DE15576
    // 2024-06-25 - Debra Frum - US26253 - Commercial Quote - Construction - Roofing Updates - Validations
    //   Added page level validations
    const { onValidate: setComponentValidation, isComponentValid } = useValidation('ENTRoofUpdatesComponent');
 
    const getHailRoofClass = useMemo(() => {
        const UpgradeTypeList = viewModelService?.productMetadata?.get('pc').types.getTypelist('HailResistantRoofCls_Ext');
        const formattedTypes =  UpgradeTypeList?.codes.map((code) => ({
                code: code.code,
                name: translator({ id: code.name })
            }))
        return formattedTypes
    },[translator, viewModelService?.productMetadata]);

    const addRoofDetailsBox = useCallback(() => {
        const update = viewModelService.create(
            {},
            'pc',
            'org.twia.edge.capabilities.policyjob.lob.homeowners.coverables.dto.TWIARoofUpdateDtlsDTO'
        );
        updateRoofDetailsVM(update);
        updateShowRoofDetails(true);
        setEditRowIndex(null);
    }, [viewModelService]);    

    const writeValue = useCallback(
        (value, path) => {

            // 2024-06-21 - Debra Frum - US26267 - Commercial Quote - Buildings and Locations – 
            //                           Construction Screen – Roofing Updates
            // Roof Upgrade Exist field isn't defined on Commercial Property
            if (!isCommercial){     
                if (isTFPA && jobType && path === 'lobData.homeowners_tfpa.coverables_Ext.construction.roofUpgradeExists') {
                    value ? setCheckBox(true) : setCheckBox(false);
                }
                if (path === 'lobData.homeowners_tfpa.coverables.construction.roofUpgradeExists') {
                    value ? setCheckBox(true) : setCheckBox(false);
                }

                if (path === 'lobData.homeowners.coverables.construction.roofUpgradeExists') {
                    value ? setCheckBox(true) : setCheckBox(false);
                    _.set(submissionVM, path, value);
                    updateWizardData(submissionVM);
                    
                    if (!value && roofData.length > 0) {
                        showAlert({
                            title: messages.roofingAlert,
                            message: messages.alertMessage,
                            status: 'warning',
                            icon: 'mi-error-outline',
                            confirmButtonText: messages.closeModal,
                        }).then((results) => {
                            if (results === 'cancel' || results === 'close') {
                                // DE15535 
                                setCheckBox(true);
                                _.set(submissionVM, path, !value);
                                updateWizardData(submissionVM);
                            }
                            setCheckBox(true);
                            _.set(submissionVM, path, !value);
                            updateWizardData(submissionVM);
                            return true;
                        })
                    }
                }
            }
            _.set(submissionVM, path, value);
            updateWizardData(submissionVM);
            refreshData();
        }, [refreshData, roofData?.length, showAlert, submissionVM, updateWizardData]
    );
    
    const saveRoofUpdates = (updatedData) => {
        if (editRowIndex !== null) {
            const updatedRoofData = roofData.map((item, index) => index === editRowIndex ? updatedData : item);
            setRoofData(updatedRoofData);
            updateRoofDetailsVM(updatedRoofData);
            if (isCommercial) {
                selectedBuilding.construction_TWIA.roofUpdateDtls_TWIA = updatedRoofData;
            } else {
                hoLOB.coverables.construction.roofUpdateDtls_TWIA.value = updatedRoofData;
            }
            updateWizardData(submissionVM);
            setEditRowIndex(null);
        } else {
            const newItem = [...roofData, updatedData.value];
            setRoofData(newItem);
            updateRoofDetailsVM(newItem);
            if (isCommercial) {
                selectedBuilding.construction_TWIA.roofUpdateDtls_TWIA = newItem;
            } else {
                hoLOB.coverables.construction.roofUpdateDtls_TWIA.value = newItem;
            }
            updateWizardData(submissionVM);
        }
        updateShowRoofDetails(false);
    };

    let oldVMData = [];

    const editRow = (index) => {
        oldVMData = _.clone(roofData[index]);
        setEditRowIndex(index);
        updateRoofDetailsVM(oldVMData); 
        updateShowRoofDetails(true);
    };

    const deleteRow = (index) => {
        showConfirm({
            title: messages.wantToDelete,
            message: messages.roofingCancelMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: messages.yesModal,
            cancelButtonText: messages.noModal, // DE17089
        }).then((results) => {
            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }
            const updatedRoofData = roofData.filter((_, i) => i !== index);
            // DE15527
            setRoofData(updatedRoofData);
            if (isCommercial) {
                selectedBuilding.construction_TWIA.roofUpdateDtls_TWIA = updatedRoofData;
            } else {
                submissionVM.value.lobData.homeowners.coverables.construction.roofUpdateDtls_TWIA = updatedRoofData;
            }
            updateWizardData(submissionVM);
            return true;
        }, _.noop)
    };
    
    const cancelRoofUpdates = () => {
        updateRoofDetailsVM(oldVMData);
        updateShowRoofDetails(false);
    };

    const getDate = (item) => {
        if (!item || !item.roofUpdateDate) {
            return 'Invalid Date';
        }
        return TWIADateFormatUtil.formatDate(item.roofUpdateDate);
    };

    
    const getUpdateType = (item) => item.roofUpdateType === 'full' ? 'Full' : 'Partial';
    // DE15474
    const getCertificate = (item) => {
        if (item.roofUpdateDate.year < 1988) {
            return '';
        }
        return item.hasWPI8CertOfCompIssued_TWIA === true ? 'Yes' : 'No'; }

    const actionButtons = (row, index) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-delete"
                onClick={() => deleteRow(index)}
                aria-label="Delete Row"
            />
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-edit"
                onClick={() => editRow(index)}
                aria-label="Edit Row"
            />
        </div>
    );
    
    useEffect(() => {
        if (roofUpgradeExists !== undefined && roofUpgradeExists) {
            setCheckBox(true);
            setRoofData(roofTableData);
        }
    }, []);

    // DE15576
    // 2024-06-25 - Debra Frum - US26253 - Commercial Quote - Construction - Roofing Updates - Validations
    //   Added page level validations
    useEffect(() => {
        if (onValidate && !_.isNull(isComponentValid)) {
            onValidate(isComponentValid, 'ENTRoofUpdatesComponent');
        }
    }, [isComponentValid, onValidate]);

    useEffect(() => {
        findInvalidComponent(isComponentValid, 'ENTRoofUpdatesComponent');
    }, [findInvalidComponent, isComponentValid])

    // DE15522
    var roofUpdatedLabel = isTFPA ? messages.roofingUpdatesTFPA : policyTypeCode === 'TWRM' ?
                           messages.roofingUpdatesMH : messages.roofingUpdates;

    // DE18751 DE18862 Roof Type should not be editable for Structure Types that have MSB
    const isRoofTypeReadOnly = () => {
        const isPolicyChange = _.get(submissionVM, 'baseData.jobType.value.code') === 'PolicyChange';
        const isTWRD = _.get(submissionVM, 'lobData.homeowners.policyType_TWIA.value.code') === 'TWRD';
        const validStructureTypes = ['1Family', '2Family', 'IndividuallyTH', 'PublicHousing'];
        const selectedStructureType = _.get(submissionVM, 'lobData.homeowners.coverables.yourHome.structureType_TWIA.value.code');
        if (!isTFPA && isTWRD && !_.isUndefined(selectedStructureType) &&
            validStructureTypes.includes(selectedStructureType)) {
            return true
        }
        if (isTFPA && isPolicyChange) {   // DE18867
            return true;
        }
        return false
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: isTFPA && jobType
        },
        // DE15630
        roofingMainContainerTWIA : {
            visible: (isCommercial && coverageFormCode !== 'TWCR' && checkbox && !isTFPA)
                  || (!isCommercial 
                        && (policyTypeCode === 'TWRD' 
                                ? ((_.includes(structureTypes, structureTypeValue) || structureTypeValue=== 'Boathouse') && checkbox && !isTFPA) 
                                : checkbox && !isTFPA))
        },
        roofingUpdatesSection: {
            visible: showRoofDetails,
            item: roofDetailsVM,
            saveData: saveRoofUpdates,
            cancelData: cancelRoofUpdates,
            isInvalid: findInvalidComponent,
            showErrors,
            onValidate
        },
        roofingUpdatesTable: {
            data: roofData,
        },
        addButtonId: {
            visible: isCommercial ? true : !showRoofDetails,
            onClick: addRoofDetailsBox
        },
        hailResistantRoofClass: {
            visible: policyTypeCode === 'TWRD' && _.includes(structureTypes, structureTypeValue),
            availableValues: getHailRoofClass
        },
        roofType: {
            readOnly : isRoofTypeReadOnly(),
            visible: showRoofType,
            required: showRoofType,
            path: isTFPA && jobType ? `${hoLOBPath}.coverables_Ext.construction.roofType` : `${hoLOBPath}.coverables.construction.roofType`
        },
        roofingUpdates: { // DE15950
            visible: !isCommercial && (policyTypeCode === 'TWRD' ? ((_.includes(structureTypes, structureTypeValue) || structureTypeValue === 'Boathouse')) : true),
            label: translator(roofUpdatedLabel),
            required: checkbox,
            showRequired: checkbox,
            path: isTFPA && jobType ? `${hoLOBPath}.coverables_Ext.construction.roofUpgradeExists` : `${hoLOBPath}.coverables.construction.roofUpgradeExists`
        },
        roofSpace: {
            visible: isTFPA
        },
        updateYear: {
            visible: checkbox && isTFPA,
            path: isTFPA && jobType ? `${hoLOBPath}.coverables_Ext.construction.roofUpgradeYear` : `${hoLOBPath}.coverables.construction.roofUpgradeYear`,
        }
    }

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            addRoofDetailsBox,
            getDate,
            getUpdateType,
            getCertificate,
            actionButtons
        },
        resolveComponentMap: { roofDetails: RoofDetails }
    }

    // 2024-06-25 - Debra Frum - US26253 - Commercial Quote - Construction - Roofing Updates - Validations
    //   Added page level validations
    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            model={submissionVM}
            onModelChange={updateWizardData}
            onValueChange={writeValue}
            showErrors={showErrors}
            onValidationChange={setComponentValidation}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
        />
    )
}

export default ENTRoofUpdatesComponent
